import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '../../components/ui/Icon';
import { useIsMobile } from '../../hooks/useIsMobile';
import { pipelinesQuery, type Pipeline } from './dealsApi';

/**
 * Popover "Sposta in un'altra pipeline".
 *
 * Serve perché i processi non si somigliano: una trattativa voucher che non
 * passa può diventare un'estensione, e gli step di arrivo sono altri. Per
 * questo si sceglie sempre anche lo step: non c'è una corrispondenza automatica
 * fra "Preventivo inviato" del voucher e quello dei rinnovi.
 */
export function MovePipelinePopover({ title, currentPipelineId, pending, onConfirm, onClose }: {
  title: string;
  currentPipelineId: string;
  pending?: boolean;
  onConfirm: (v: { pipeline: string; pipelineName: string; stageId: string }) => void;
  onClose: () => void;
}) {
  const { data: pipelines } = useQuery(pipelinesQuery);
  const others = useMemo<Pipeline[]>(() => (pipelines ?? []).filter((p) => p.id !== currentPipelineId), [pipelines, currentPipelineId]);
  const [slug, setSlug] = useState('');
  const [stageId, setStageId] = useState('');
  const mobile = useIsMobile();

  // Alla prima pipeline disponibile, primo step
  useEffect(() => {
    if (slug || !others.length) return;
    const first = others[0]!;
    setSlug(first.slug);
    setStageId(first.stages[0]?.id ?? '');
  }, [others, slug]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const target = others.find((p) => p.slug === slug);
  const pickPipeline = (s: string) => {
    setSlug(s);
    setStageId(others.find((p) => p.slug === s)?.stages[0]?.id ?? '');
  };

  const submit = () => {
    if (pending || !target || !stageId) return;
    onConfirm({ pipeline: target.slug, pipelineName: target.name, stageId });
  };

  // Sul telefono è un foglio dal basso, in un portale: dentro la pagina il
  // bottone "Sposta" finirebbe sotto la barra in basso. Vedi NextActionPopover.
  const corpo = (
    <>
      <div className="fixed inset-0 z-[60]" onClick={onClose} />
      <div className="dropdown adm-pop right-0 mt-1 z-[61]" role="dialog" aria-label="Sposta in un'altra pipeline"
        style={{ width: 'min(420px, calc(100vw - 32px))' }}>
        <div className="adm-pop-hd">
          <span className="t-micro">Sposta</span>
          <span className="font-semibold truncate">{title}</span>
        </div>
        <div className="adm-pop-grid">
          <label className="field-label">Pipeline</label>
          <select className="inp inp-sm" value={slug} onChange={(e) => pickPipeline(e.target.value)} autoFocus>
            {others.map((p) => <option key={p.id} value={p.slug}>{p.name}</option>)}
          </select>
          <label className="field-label">Step</label>
          <select className="inp inp-sm" value={stageId} onChange={(e) => setStageId(e.target.value)}>
            {(target?.stages ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="px-4 pb-3 text-[12px] mute" style={{ lineHeight: 1.45 }}>
          Storico e azioni programmate restano attaccati alla trattativa.
        </div>
        <div className="adm-pop-ft">
          <button className="btn btn-sm btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-sm" disabled={pending || !target || !stageId} onClick={submit}>
            <Icon name="arrowRight" size={10} /> Sposta
          </button>
        </div>
      </div>
    </>
  );
  return mobile ? createPortal(corpo, document.body) : corpo;
}
